import React, { createContext, useState, useContext, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  // Lightweight authenticated heartbeat while the user is logged in.
  // Updates `last_seen_at` server-side so Admin sees ONLINE / Last Seen
  // without polling Twelve Data. Fires immediately on login and every 45s.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const beat = async () => {
      try { await axios.post(`${API}/session/heartbeat`); } catch (_e) { /* ignore */ }
    };
    beat();
    const id = setInterval(() => { if (!cancelled) beat(); }, 45000);
    // Also beat when the tab regains focus so returning users flip to
    // ONLINE within a second instead of waiting for the next interval.
    const onFocus = () => { if (!cancelled) beat(); };
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { access_token, user: newUser } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(newUser);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    return newUser;
  };

  const refresh = useCallback(fetchUser, []);

  const logout = useCallback(() => {
    // Best-effort: tell backend the session ended so Admin flips this
    // client to OFFLINE immediately instead of waiting 90 s.
    try { axios.post(`${API}/session/logout`).catch(() => {}); } catch (_e) {}
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
    // Force full navigation so any cached protected pages are cleared
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.replace('/login');
    }
  }, []);

  const contextValue = useMemo(
    () => ({ user, loading, login, register, logout, refresh }),
    [user, loading, logout, refresh]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};